import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
    FiPhone,
    FiCreditCard,
    FiMap,
    FiUsers,
    FiSettings,
    FiLock,
    FiSmartphone,
    FiHelpCircle,
    FiUserPlus,
    FiActivity,
    FiClock,
    FiDollarSign,
    FiMapPin,
    FiNavigation,
    FiShield,
    FiHome,
    FiBarChart2,
    FiCheckCircle,
    FiX,
    FiChevronLeft,
    FiChevronRight,
} from "react-icons/fi";

// Import translations
import orbitFa from "./locales/CircularFeatureOrbit-fa.json";
import orbitEn from "./locales/CircularFeatureOrbit-en.json";
import orbitAr from "./locales/CircularFeatureOrbit-ar.json";
import orbitTr from "./locales/CircularFeatureOrbit-tr.json";

// Design Tokens
const tokens = {
    colors: {
        primaryBlue: "#0038A8",
        techBlue: "#0072CE",
        signalCyan: "#00E5FF",
        deepNavy: "#002B5C",
        lightBlue: "#EBF5FF",
        softBorder: "#D0E7FF",
        coolGray: "#64748B",
        cardBg: "rgba(255, 255, 255, 0.92)",
    },
    zIndex: {
        base: 1,
        rings: 10,
        phone: 20,
        buttons: 40,
        panel: 50,
    },
};

// Feature Button Component (Desktop Orbit View)
const DesktopFeatureButton: React.FC<{
    feature: any;
    isSelected: boolean;
    onClick: () => void;
    position: { x: number; y: number };
}> = ({ feature, isSelected, onClick, position }) => {
    return (
        <motion.button
            className="absolute flex flex-col items-center text-center z-40 cursor-pointer"
            style={{
                x: position.x,
                y: position.y,
                width: "140px",
            }}
            whileHover={{ scale: 1.06, y: position.y - 4 }}
            whileTap={{ scale: 0.95 }}
            onClick={onClick}
        >
            <motion.div
                className="w-16 h-16 rounded-2xl flex items-center justify-center text-white mb-2 relative overflow-hidden shadow-md"
                style={{
                    background: isSelected
                        ? "linear-gradient(135deg, #0038A8, #0072CE)"
                        : `linear-gradient(135deg, ${feature.color}, ${feature.color}dd)`,
                    boxShadow: isSelected
                        ? "0 10px 25px rgba(0, 114, 206, 0.35), inset 0 2px 4px rgba(255,255,255,0.4)"
                        : "0 4px 14px rgba(0, 56, 168, 0.15), inset 0 1px 2px rgba(255,255,255,0.3)",
                    border: isSelected
                        ? "2px solid #00E5FF"
                        : "2px solid rgba(255,255,255,0.9)",
                }}
                animate={
                    isSelected
                        ? {
                              scale: [1, 1.06, 1],
                          }
                        : {}
                }
                transition={{
                    duration: 2,
                    repeat: isSelected ? Infinity : 0,
                    repeatType: "reverse",
                }}
            >
                <div className="text-2xl text-white drop-shadow-sm">{feature.icon}</div>

                {isSelected && (
                    <motion.div
                        className="absolute inset-0 rounded-2xl border-2 border-[#00E5FF]"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1.25, opacity: 0 }}
                        transition={{
                            duration: 1.6,
                            repeat: Infinity,
                            repeatDelay: 0.4,
                        }}
                    />
                )}
            </motion.div>

            <motion.div
                className="backdrop-blur-md rounded-xl p-2.5 shadow-sm border text-center w-full"
                style={{
                    background: isSelected ? "rgba(239, 246, 255, 0.95)" : "rgba(255, 255, 255, 0.92)",
                    borderColor: isSelected ? "#0072CE" : "#E2E8F0",
                    boxShadow: isSelected
                        ? "0 6px 16px rgba(0, 114, 206, 0.2)"
                        : "0 2px 8px rgba(0, 0, 0, 0.05)",
                }}
            >
                <p
                    className="text-xs font-bold leading-tight"
                    style={{
                        color: isSelected ? "#0038A8" : "#1E293B",
                    }}
                >
                    {feature.title}
                </p>
                <p className="text-[11px] leading-tight text-slate-500 mt-1 line-clamp-2">
                    {feature.description}
                </p>
            </motion.div>
        </motion.button>
    );
};

// Orbit Ring Component
const OrbitRing: React.FC<{
    index: number;
}> = ({ index }) => {
    return (
        <motion.div
            className="absolute rounded-full pointer-events-none"
            style={{
                width: `${440 + index * 140}px`,
                height: `${440 + index * 140}px`,
                border: "1.5px dashed rgba(0, 114, 206, 0.18)",
                zIndex: tokens.zIndex.rings,
            }}
            animate={{
                rotate: index % 2 === 0 ? 360 : -360,
            }}
            transition={{
                duration: 40 + index * 12,
                repeat: Infinity,
                ease: "linear",
            }}
        />
    );
};

// Feature Detail Screen (Inside Phone)
const FeatureDetailPanel: React.FC<{
    feature: any;
    onClose: () => void;
}> = ({ feature, onClose }) => {
    const { t } = useTranslation("circularOrbit");

    return (
        <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 350, damping: 28 }}
            className="absolute inset-0 bg-white z-30 flex flex-col overflow-hidden text-start rounded-[32px]"
        >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0038A8] via-[#0052CC] to-[#0072CE] p-3.5 sm:p-4 text-white relative flex items-center justify-between shadow-sm">
                <div className="flex items-center gap-2.5 min-w-0 pr-2">
                    <div className="w-9 h-9 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center text-lg text-white shrink-0 shadow-xs">
                        {feature.icon}
                    </div>
                    <div className="min-w-0">
                        <h3 className="font-bold text-xs sm:text-sm text-white leading-tight truncate">
                            {feature.title}
                        </h3>
                        <p className="text-[10px] sm:text-[11px] text-blue-100 opacity-90 truncate">
                            {feature.description}
                        </p>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="p-1.5 rounded-full bg-white/15 hover:bg-white/25 active:scale-95 text-white transition-all shrink-0 cursor-pointer"
                    aria-label={t("closePanel", { defaultValue: "بستن" })}
                >
                    <FiX size={16} />
                </button>
            </div>

            {/* Content Details List */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-3.5 space-y-2 bg-slate-50/70">
                {feature.details.map((detail: any, index: number) => (
                    <motion.div
                        key={detail.title || index}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.04 }}
                        className="p-2.5 sm:p-3 rounded-xl bg-white border border-blue-100/90 shadow-2xs flex items-start gap-2.5 hover:border-blue-200 transition-colors"
                    >
                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br from-[#0038A8] to-[#0072CE] text-white flex items-center justify-center text-xs sm:text-sm shrink-0 mt-0.5 shadow-2xs">
                            {detail.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                            <h4 className="font-bold text-xs text-slate-900 leading-tight">
                                {detail.title}
                            </h4>
                            <p className="text-[11px] text-slate-500 leading-relaxed mt-0.5">
                                {detail.description}
                            </p>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Bottom Screen Bar */}
            <div className="p-2.5 sm:p-3 bg-white border-t border-slate-100 flex justify-center">
                <button
                    onClick={onClose}
                    className="w-full py-1.5 sm:py-2 rounded-xl bg-blue-50 hover:bg-blue-100 active:bg-blue-200 text-[#0072CE] font-bold text-xs transition-colors text-center cursor-pointer"
                >
                    {t("closePanel", { defaultValue: "بازگشت به منو" })}
                </button>
            </div>
        </motion.div>
    );
};

const CircularFeatureOrbit: React.FC = () => {
    const { t, i18n } = useTranslation("circularOrbit");
    const isRTL = ["fa", "ar"].includes(i18n.language);

    const [selectedFeature, setSelectedFeature] = useState<string | null>("location-safety");
    const containerRef = useRef<HTMLDivElement>(null);

    // Load translations
    useEffect(() => {
        const ns = "circularOrbit";
        const resources = {
            fa: orbitFa,
            en: orbitEn,
            ar: orbitAr,
            tr: orbitTr,
        };

        Object.entries(resources).forEach(([lng, data]) => {
            if (!i18n.hasResourceBundle(lng, ns)) {
                i18n.addResourceBundle(lng, ns, data, true, true);
            }
        });
    }, [i18n]);

    // Features data with complete details
    const features = [
        {
            id: "location-safety",
            title: t("features.locationSafety.title"),
            description: t("features.locationSafety.description"),
            icon: <FiMap size={22} />,
            color: "#0072CE",
            gradient: "linear-gradient(135deg, #0038A8 0%, #0072CE 100%)",
            details: [
                {
                    title: t("features.locationSafety.details.0.title"),
                    description: t("features.locationSafety.details.0.description"),
                    icon: <FiMapPin />,
                },
                {
                    title: t("features.locationSafety.details.1.title"),
                    description: t("features.locationSafety.details.1.description"),
                    icon: <FiHome />,
                },
                {
                    title: t("features.locationSafety.details.2.title"),
                    description: t("features.locationSafety.details.2.description"),
                    icon: <FiNavigation />,
                },
                {
                    title: t("features.locationSafety.details.3.title"),
                    description: t("features.locationSafety.details.3.description"),
                    icon: <FiShield />,
                },
            ],
        },
        {
            id: "easy-payments",
            title: t("features.easyPayments.title"),
            description: t("features.easyPayments.description"),
            icon: <FiCreditCard size={22} />,
            color: "#0038A8",
            gradient: "linear-gradient(135deg, #002B5C 0%, #0052CC 100%)",
            details: [
                {
                    title: t("features.easyPayments.details.0.title"),
                    description: t("features.easyPayments.details.0.description"),
                    icon: <FiClock />,
                },
                {
                    title: t("features.easyPayments.details.1.title"),
                    description: t("features.easyPayments.details.1.description"),
                    icon: <FiUsers />,
                },
                {
                    title: t("features.easyPayments.details.2.title"),
                    description: t("features.easyPayments.details.2.description"),
                    icon: <FiDollarSign />,
                },
                {
                    title: t("features.easyPayments.details.3.title"),
                    description: t("features.easyPayments.details.3.description"),
                    icon: <FiLock />,
                },
            ],
        },
        {
            id: "family-care",
            title: t("features.familyCare.title"),
            description: t("features.familyCare.description"),
            icon: <FiUsers size={22} />,
            color: "#0052CC",
            gradient: "linear-gradient(135deg, #0038A8 0%, #0072CE 100%)",
            details: [
                {
                    title: t("features.familyCare.details.0.title"),
                    description: t("features.familyCare.details.0.description"),
                    icon: <FiSettings />,
                },
                {
                    title: t("features.familyCare.details.1.title"),
                    description: t("features.familyCare.details.1.description"),
                    icon: <FiLock />,
                },
                {
                    title: t("features.familyCare.details.2.title"),
                    description: t("features.familyCare.details.2.description"),
                    icon: <FiBarChart2 />,
                },
                {
                    title: t("features.familyCare.details.3.title"),
                    description: t("features.familyCare.details.3.description"),
                    icon: <FiCheckCircle />,
                },
            ],
        },
        {
            id: "instant-help",
            title: t("features.instantHelp.title"),
            description: t("features.instantHelp.description"),
            icon: <FiPhone size={22} />,
            color: "#0072CE",
            gradient: "linear-gradient(135deg, #0052CC 0%, #00E5FF 100%)",
            details: [
                {
                    title: t("features.instantHelp.details.0.title"),
                    description: t("features.instantHelp.details.0.description"),
                    icon: <FiSmartphone />,
                },
                {
                    title: t("features.instantHelp.details.1.title"),
                    description: t("features.instantHelp.details.1.description"),
                    icon: <FiHelpCircle />,
                },
                {
                    title: t("features.instantHelp.details.2.title"),
                    description: t("features.instantHelp.details.2.description"),
                    icon: <FiUserPlus />,
                },
                {
                    title: t("features.instantHelp.details.3.title"),
                    description: t("features.instantHelp.details.3.description"),
                    icon: <FiActivity />,
                },
                {
                    title: t("features.instantHelp.details.4.title"),
                    description: t("features.instantHelp.details.4.description"),
                    icon: <FiPhone />,
                },
            ],
        },
    ];

    const selectedFeatureData = features.find((f) => f.id === selectedFeature);
    const activeIndex = features.findIndex((f) => f.id === selectedFeature);

    const nextFeature = () => {
        const nextIdx = (activeIndex + 1) % features.length;
        setSelectedFeature(features[nextIdx].id);
    };

    const prevFeature = () => {
        const prevIdx = (activeIndex - 1 + features.length) % features.length;
        setSelectedFeature(features[prevIdx].id);
    };

    // Desktop button positions
    const getDesktopButtonPosition = (index: number) => {
        if (index === 0) {
            return { x: -320, y: 0 };
        } else if (index === 1) {
            return { x: -170, y: -180 };
        } else if (index === 2) {
            return { x: 170, y: -180 };
        } else {
            return { x: 320, y: 0 };
        }
    };

    return (
        <div
            ref={containerRef}
            className="relative w-full overflow-hidden py-6 sm:py-10"
            dir={isRTL ? "rtl" : "ltr"}
        >
            {/* ══════════════════════════════════════════════════════════════
                MOBILE DEDICATED VIEW (< md screens)
                ══════════════════════════════════════════════════════════════ */}
            <div className="block md:hidden max-w-md mx-auto px-2">
                {/* 2x2 Feature Selector Cards */}
                <div className="grid grid-cols-2 gap-2.5 mb-5">
                    {features.map((feature) => {
                        const isSelected = selectedFeature === feature.id;
                        return (
                            <motion.button
                                key={feature.id}
                                onClick={() =>
                                    setSelectedFeature(
                                        isSelected ? null : feature.id
                                    )
                                }
                                whileTap={{ scale: 0.97 }}
                                className={`relative p-3 rounded-2xl text-start transition-all duration-300 overflow-hidden cursor-pointer border ${
                                    isSelected
                                        ? "bg-blue-50/90 border-[#0072CE] shadow-md ring-2 ring-[#0072CE]/20"
                                        : "bg-white/90 border-slate-200/80 shadow-xs hover:border-blue-200"
                                }`}
                            >
                                <div className="flex items-center gap-2.5 mb-1.5">
                                    <div
                                        className="w-9 h-9 rounded-xl flex items-center justify-center text-white shrink-0 shadow-xs"
                                        style={{ background: feature.gradient }}
                                    >
                                        {React.cloneElement(feature.icon, { size: 18 })}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <h3
                                            className={`font-bold text-xs leading-tight truncate ${
                                                isSelected ? "text-[#0038A8]" : "text-slate-900"
                                            }`}
                                        >
                                            {feature.title}
                                        </h3>
                                    </div>
                                </div>
                                <p className="text-[11px] text-slate-500 leading-snug line-clamp-2">
                                    {feature.description}
                                </p>
                            </motion.button>
                        );
                    })}
                </div>

                {/* Mobile Phone Mockup Screen */}
                <div className="relative flex flex-col items-center">
                    <div className="relative w-[270px] h-[480px] bg-white rounded-[36px] shadow-2xl border-[6px] border-slate-900 overflow-hidden">
                        {/* Phone Notch */}
                        <div className="absolute top-0 inset-x-0 h-4 flex justify-center z-40 pointer-events-none">
                            <div className="w-20 h-3 bg-slate-900 rounded-b-xl" />
                        </div>

                        {/* Default Phone Screen */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center p-5 bg-gradient-to-b from-blue-50/60 via-white to-blue-50/40 overflow-hidden">
                            <motion.div
                                className="absolute w-48 h-48 rounded-full bg-blue-500/10 blur-2xl"
                                animate={{
                                    scale: [1, 1.2, 1],
                                    opacity: [0.3, 0.6, 0.3],
                                }}
                                transition={{
                                    duration: 5,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                }}
                            />

                            <motion.div
                                className="relative w-28 h-28 flex items-center justify-center mb-3"
                                animate={{
                                    y: [0, -5, 0],
                                }}
                                transition={{
                                    duration: 3,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                }}
                            >
                                <img
                                    src="media/Logo1.svg"
                                    alt={t("logoAlt", { defaultValue: "Delband Logo" })}
                                    className="w-full h-full object-contain drop-shadow-md"
                                />
                            </motion.div>

                            <p className="text-xs font-bold text-[#0038A8] text-center px-4">
                                {t("guideText", { defaultValue: "برای مشاهده جزئیات، کارت‌های بالا را انتخاب کنید" })}
                            </p>
                        </div>

                        {/* Interactive Feature Screen inside Phone */}
                        <AnimatePresence>
                            {selectedFeature && selectedFeatureData && (
                                <FeatureDetailPanel
                                    feature={selectedFeatureData}
                                    onClose={() => setSelectedFeature(null)}
                                />
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Navigation Pills under phone */}
                    <div className="flex items-center justify-center gap-3 mt-4">
                        <button
                            onClick={isRTL ? nextFeature : prevFeature}
                            className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 active:scale-95 transition-all text-xs flex items-center gap-1 cursor-pointer font-medium"
                        >
                            {isRTL ? <FiChevronRight /> : <FiChevronLeft />}
                            <span>قبلی</span>
                        </button>

                        <div className="flex items-center gap-1.5">
                            {features.map((f, i) => (
                                <button
                                    key={f.id}
                                    onClick={() => setSelectedFeature(f.id)}
                                    className="h-2 rounded-full transition-all duration-300 cursor-pointer"
                                    style={{
                                        width: selectedFeature === f.id ? "18px" : "6px",
                                        backgroundColor: selectedFeature === f.id ? "#0072CE" : "#CBD5E1",
                                    }}
                                />
                            ))}
                        </div>

                        <button
                            onClick={isRTL ? prevFeature : nextFeature}
                            className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 active:scale-95 transition-all text-xs flex items-center gap-1 cursor-pointer font-medium"
                        >
                            <span>بعدی</span>
                            {isRTL ? <FiChevronLeft /> : <FiChevronRight />}
                        </button>
                    </div>
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════
                DESKTOP ORBITAL VIEW (>= md screens)
                ══════════════════════════════════════════════════════════════ */}
            <div className="hidden md:flex relative w-full min-h-[750px] lg:min-h-[820px] flex-col items-center justify-center">
                {/* Soft Blue Orbit Rings Background */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    {[1, 2, 3].map((ring, index) => (
                        <OrbitRing key={`orbit-ring-${ring}`} index={index} />
                    ))}
                </div>

                {/* Phone Frame */}
                <div
                    className="relative w-[280px] h-[540px] bg-white rounded-[38px] shadow-2xl border-[7px] border-slate-900/90 overflow-hidden"
                    style={{ zIndex: tokens.zIndex.phone }}
                >
                    {/* Phone Notch */}
                    <div className="absolute top-0 inset-x-0 h-4 flex justify-center z-40 pointer-events-none">
                        <div className="w-24 h-3.5 bg-slate-900 rounded-b-xl" />
                    </div>

                    {/* Default Phone Idle Screen */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-6 bg-gradient-to-b from-blue-50/50 via-white to-blue-50/30 overflow-hidden">
                        <motion.div
                            className="absolute w-64 h-64 rounded-full bg-blue-500/10 blur-2xl"
                            animate={{
                                scale: [1, 1.2, 1],
                                opacity: [0.3, 0.6, 0.3],
                            }}
                            transition={{
                                duration: 6,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                        />

                        <motion.div
                            className="relative w-36 h-36 flex items-center justify-center mb-4"
                            animate={{
                                y: [0, -6, 0],
                            }}
                            transition={{
                                duration: 3.5,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                        >
                            <img
                                src="media/Logo1.svg"
                                alt={t("logoAlt", { defaultValue: "Delband Logo" })}
                                className="w-full h-full object-contain drop-shadow-md"
                            />
                        </motion.div>

                        <p className="text-xs font-bold text-[#0038A8] text-center opacity-80">
                            {t("guideText", { defaultValue: "برای مشاهده آیکون‌ها را انتخاب کنید" })}
                        </p>
                    </div>

                    {/* Native Screen Drawer Panel inside Phone */}
                    <AnimatePresence>
                        {selectedFeature && selectedFeatureData && (
                            <FeatureDetailPanel
                                feature={selectedFeatureData}
                                onClose={() => setSelectedFeature(null)}
                            />
                        )}
                    </AnimatePresence>
                </div>

                {/* Section Title Under Phone in Desktop */}
                <motion.h2
                    className="relative z-10 text-xl md:text-2xl font-bold text-slate-900 text-center px-4 mt-8"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                >
                    {t("mainTitle")}
                    <div className="h-1 w-20 bg-gradient-to-r from-[#0038A8] to-[#0072CE] rounded-full mx-auto mt-2.5" />
                </motion.h2>

                {/* Orbiting Feature Buttons */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="relative w-full h-full flex items-center justify-center pointer-events-auto">
                        {features.map((feature, index) => (
                            <DesktopFeatureButton
                                key={feature.id}
                                feature={feature}
                                isSelected={selectedFeature === feature.id}
                                onClick={() =>
                                    setSelectedFeature(
                                        feature.id === selectedFeature ? null : feature.id
                                    )
                                }
                                position={getDesktopButtonPosition(index)}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CircularFeatureOrbit;

